import { errorLogger, type ErrorLog } from './errorLogger';
import { getBuildTimestamp } from './build-info';

export interface DiagnosticsReport {
  generatedAt: string;
  appVersion: string;
  summary: ReturnType<typeof errorLogger.getDiagnosticsSummary>;
  logs: ErrorLog[];
}

export interface DiagnosticsEmailContext {
  errorId?: string;
  message?: string;
  timestamp?: string;
  url?: string;
  appVersion?: string;
  userDescription?: string;
  recentActions?: string;
}

export function buildDiagnosticsReport(): DiagnosticsReport {
  return {
    generatedAt: new Date().toISOString(),
    appVersion:
      import.meta.env.VITE_APP_VERSION ??
      import.meta.env.VITE_BUILD_TIMESTAMP ??
      getBuildTimestamp(),
    summary: errorLogger.getDiagnosticsSummary(),
    logs: errorLogger.getErrorLogs(),
  };
}

export function buildDiagnosticsJson(report?: DiagnosticsReport): string {
  const payload = report ?? buildDiagnosticsReport();
  return JSON.stringify(payload, null, 2);
}

export function buildDiagnosticsFileName(timestamp?: string): string {
  const datePart = (timestamp ?? new Date().toISOString()).split('T')[0];
  return `diagnostics-${datePart}.json`;
}

/**
 * Format hardware info for the email report
 */
function formatHardwareInfo(
  systemInfo: ReturnType<typeof errorLogger.getDiagnosticsSummary>['systemInfo']
): string {
  const lines: string[] = [];

  lines.push(`Platform: ${systemInfo.platform}`);
  lines.push(`Screen: ${systemInfo.screenResolution} @ ${systemInfo.devicePixelRatio}x DPI`);

  if (systemInfo.hardwareConcurrency) {
    lines.push(`CPU cores: ${systemInfo.hardwareConcurrency}`);
  }
  if (systemInfo.deviceMemory) {
    lines.push(`RAM: ${systemInfo.deviceMemory}GB`);
  }
  if (systemInfo.gpu) {
    lines.push(`GPU: ${systemInfo.gpu.renderer}`);
    lines.push(`GPU vendor: ${systemInfo.gpu.vendor}`);
  }
  if (systemInfo.maxTouchPoints > 0) {
    lines.push(`Touch: ${systemInfo.maxTouchPoints} points`);
  }

  return lines.join('\n');
}

export function buildDiagnosticsEmailPayload(
  userDescription?: string,
  context?: DiagnosticsEmailContext
): { subject: string; body: string } {
  const report = buildDiagnosticsReport();
  const latestError = report.logs[0];
  const resolvedDescription = context?.userDescription ?? (userDescription?.trim() || '—');

  const resolvedErrorId = context?.errorId ?? latestError?.id ?? 'nessuno';
  const resolvedMessage = context?.message ?? latestError?.message ?? 'Nessun errore registrato';
  const resolvedTimestamp = context?.timestamp ?? latestError?.timestamp ?? report.generatedAt;
  const resolvedUrl = context?.url ?? latestError?.url ?? report.summary.sessionInfo.url;
  const resolvedVersion = context?.appVersion ?? report.appVersion;

  const recentActions = context?.recentActions || '';
  const actionsSection = recentActions ? `\n\nAzioni recenti:\n${recentActions}` : '';

  // Include hardware info to identify patterns across office machines
  const hardwareInfo = formatHardwareInfo(report.summary.systemInfo);
  const hardwareSection = `\n\nInfo sistema:\n${hardwareInfo}`;

  // Include stack trace if available
  const stackSection = latestError?.stack
    ? `\n\nStack trace:\n${latestError.stack.slice(0, 1500)}`
    : '';

  // Deduplicated recent errors summary
  const recentErrorsSummary = buildDeduplicatedErrorsSummary(report.logs);
  const recentErrorsSection = recentErrorsSummary
    ? `\n\nErrori recenti (deduplicati):\n${recentErrorsSummary}`
    : '';

  const subject = `[LM Sistema Tecnico] Segnalazione errore - ${resolvedErrorId}`;
  const body = `Errore: ${resolvedMessage}\nData: ${resolvedTimestamp}\nURL: ${resolvedUrl}\nVersione: ${resolvedVersion}${actionsSection}${hardwareSection}${stackSection}${recentErrorsSection}\n\nDescrizione utente:\n${resolvedDescription}\n\n---\nReport completo disponibile in Impostazioni > Supporto > Scarica diagnostica`;

  return { subject, body };
}

/**
 * Build a deduplicated summary of recent errors.
 * Groups errors by message and shows count for each unique error.
 */
function buildDeduplicatedErrorsSummary(logs: ErrorLog[]): string {
  if (logs.length === 0) return '';

  // Group by error message to deduplicate
  const errorCounts = new Map<string, { count: number; lastSeen: string }>();

  for (const log of logs) {
    if (log.severity !== 'error') continue;
    const existing = errorCounts.get(log.message);
    if (existing) {
      existing.count++;
      if (log.timestamp > existing.lastSeen) {
        existing.lastSeen = log.timestamp;
      }
    } else {
      errorCounts.set(log.message, { count: 1, lastSeen: log.timestamp });
    }
  }

  if (errorCounts.size === 0) return '';

  // Format as list (limit to 5 unique errors)
  const lines: string[] = [];
  let count = 0;
  for (const [message, { count: occurrences }] of errorCounts.entries()) {
    if (count >= 5) break;
    const truncatedMsg = message.length > 80 ? message.slice(0, 80) + '...' : message;
    lines.push(`• ${truncatedMsg} (x${occurrences})`);
    count++;
  }

  if (errorCounts.size > 5) {
    lines.push(`... e altri ${errorCounts.size - 5} errori unici`);
  }

  return lines.join('\n');
}

export function buildDiagnosticsShareSummary(context?: DiagnosticsEmailContext): string {
  const report = buildDiagnosticsReport();
  const latestError = report.logs[0];
  const errorId = context?.errorId ?? latestError?.id ?? 'nessuno';
  const message = context?.message ?? latestError?.message ?? 'Nessun errore';
  const timestamp = context?.timestamp ?? latestError?.timestamp ?? report.generatedAt;
  const url = context?.url ?? latestError?.url ?? report.summary.sessionInfo.url;
  const version = context?.appVersion ?? report.appVersion;

  return `Errore: ${message}\nID: ${errorId}\nData: ${timestamp}\nURL: ${url}\nVersione: ${version}`;
}
